import * as Highcharts from 'highcharts';

// Initialize Highcharts Maps module (side-effect import — registers map chart type)
import 'highcharts/modules/map';
import 'highcharts/modules/marker-clusters';

/**
 * Detect if we are running inside Storybook (local or Chromatic).
 * Used to enable deterministic SVG IDs for visual regression testing.
 */
function isStorybook(): boolean {
  return typeof window !== 'undefined' && !!(window as unknown as { __STORYBOOK_CLIENT_API__?: unknown }).__STORYBOOK_CLIENT_API__;
}

/**
 * Initialize Highcharts with global configuration
 */
function initializeHighcharts() {
  // Use serial IDs in Storybook/Chromatic so SVG element IDs are deterministic.
  // Stops Chromatic from flagging false positives when Highcharts.uniqueKey() changes.
  // See https://api.highcharts.com/class-reference/Highcharts#.useSerialIds
  if (isStorybook()) {
    Highcharts.useSerialIds(true);
  }

  // Set global options
  Highcharts.setOptions({
    chart: {
      backgroundColor: '#ffffff',
      style: {
        fontFamily: '-apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      },
      animation: {
        duration: 150
      },
      panning: {
        enabled: false // Disable panning to avoid touch event listeners
      },
      // pinchType: null, // Disable pinch zoom
      // zoomType: null, // Disable zoom
      events: {
        load() {
          if (this.container) {
            // Set touch-action CSS property to allow native scroll
            this.container.style.touchAction = 'pan-y pinch-zoom';
          }
        }
      }
    },
    title: {
      style: {
        fontSize: '18px',
        fontWeight: '500'
      }
    },
    subtitle: {
      style: {
        fontSize: '14px',
        fontWeight: 'normal'
      }
    },
    xAxis: {
      labels: {
        style: {
          fontSize: '12px'
        }
      },
    },
    yAxis: {
      labels: {
        style: {
          fontSize: '12px'
        }
      }
    },
    legend: {
      itemStyle: {
        fontSize: '12px',
        fontWeight: 'normal'
      }
    },
    plotOptions: {
      series: {
        animation: {
          duration: 150
        },
        states: {
          hover: {
            enabled: true,
            brightness: 0.1
          },
          inactive: {
            opacity: 0.5
          }
        },
        marker: {
          radius: 4,
          symbol: 'circle'
        },
        stickyTracking: false, // Disable sticky tracking
        enableMouseTracking: true,
        point: {
          events: {
            click() {
              return false; // Prevent default click behavior
            }
          }
        }
      }
    },
    tooltip: {
      animation: true,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      borderWidth: 0,
      shadow: true,
      style: {
        fontSize: '12px'
      },
      useHTML: true,
      followTouchMove: false, // Disable tooltip following touch
      outside: true, // Render tooltip outside of chart area
      hideDelay: 0 // Remove delay to improve touch response
    },
    credits: {
      enabled: false
    },
    accessibility: {
      enabled: false,
    },
    // Performance optimizations
    boost: {
      enabled: true,
      useGPUTranslations: true,
      usePreallocated: true
    }
  });
}

// Initialize Highcharts when the module is imported
initializeHighcharts();

// Export the configured Highcharts instance
export default Highcharts;
